import React, { useMemo } from "react";

import _ from "lodash";
import { useTranslation } from "react-i18next";
import { BiPlus } from "react-icons/bi";
import { v4 as uuid } from "uuid";

import { handleGetConvertValue } from "../../../../../../util/converts";
import { ShipmentDetails } from "../../../../../../util/enums";
import { DecimalFormatType } from "../../../../../../util/formatDecimalValue";
import { getMeasurement, getWeightMeasurement } from "../../../../../../util/getMeasurement";
import { ShipmentObjects } from "../../../../../Booking/util";
import { Color } from "../../../../../Form/Button";
import CollapsablePanel from "../../../../../Generic/Collapse";
import { useShipmentContext } from "../../../../hooks/useShipmentContext";
import ContainerDetails from "./components/Details";
import { ContainerInfoView } from "./components/Details/view";
import {
  MainContainer,
  Title,
  CountBadge,
  CollapseBody,
  AddContainerButton,
  ButtonSection,
  useStyles,
  Summary,
  Divider,
  ContainerFragment,
} from "./styles";

interface ShipmentContainersProps {
  isView?: boolean;
}

const Containers = ({ isView = false }: ShipmentContainersProps) => {
  const styles = useStyles()();

  const { t: translate } = useTranslation();

  const { shipment, handleUpdateShipment, handleChangeOptions } = useShipmentContext();

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const summaryValues = useMemo(() => {
    const values = {
      totalWeight: 0,
      volumetricWeight: 0,
      grossVolume: 0,
    };

    if (shipment && shipment?.containers) {
      for (const container of shipment.containers) {
        values.totalWeight += Number(container.grossWeight || 0);
        values.volumetricWeight += Number(container.volumetricWeight || 0);
        values.grossVolume += Number(container.grossVolume || 0);
      }
    }

    return values;
  }, [shipment?.containers]);

  const handleAddContainer = () => {
    const containers = _.cloneDeep(shipment?.containers || [{}]);

    containers.push({ uuid: uuid() });

    handleUpdateShipment(ShipmentDetails.CONTAINERS, containers);
    handleChangeOptions(ShipmentObjects.CONTAINERS);
  };

  const handleDeleteContainer = (uuid?: string) => {
    const containers = _.cloneDeep(shipment?.containers || []);
    const newContainers = containers?.filter((value) => value?.uuid !== uuid);

    handleUpdateShipment("all", {
      [ShipmentDetails.CONTAINERS]: newContainers,
      [ShipmentDetails.CONTAINER_NUMBERS]: containers
        .map((item) => item?.container)
        .filter((value) => value !== "")
        .join("-"),
    });

    handleChangeOptions(ShipmentObjects.CONTAINERS);
  };

  const Header = (
    <div className="d-flex justify-content-between align-items-center w-100">
      <div className="d-flex align-items-baseline">
        <Title>{translate("containers")}</Title>
        <CountBadge>
          <span>{shipment?.containers?.length || 0}</span>
        </CountBadge>
      </div>
      <div className="d-flex">
        <Summary>
          <div>
            <span>{translate("totalWeight")}</span>
            <span>
              {handleGetConvertValue(
                Number(summaryValues.totalWeight),
                measurementWeight?.code,
                DecimalFormatType.MAERSK
              )}
              {` ${(measurementWeight?.code || "")?.toUpperCase()}`}
            </span>
          </div>
          <div>
            <span>{translate("volumetricWeight")}</span>
            <span>
              {handleGetConvertValue(
                Number(summaryValues.volumetricWeight),
                measurementWeight?.code,
                DecimalFormatType.MAERSK
              )}
              {` ${(measurementWeight?.code || "")?.toUpperCase()}`}
            </span>
          </div>
          <div>
            <span>{translate("grossVolume")}</span>
            <span>
              {handleGetConvertValue(
                Number(summaryValues.grossVolume),
                measurement?.code,
                DecimalFormatType.MAERSK
              )}
              {` ${(measurement?.code || "")?.toUpperCase()}`}
            </span>
          </div>
        </Summary>
      </div>
    </div>
  );

  const containers = shipment?.containers;

  return (
    <MainContainer item container>
      <CollapsablePanel panelClassName={styles.panel} filledBackground title={Header}>
        <CollapseBody>
          <div className="d-flex flex-column">
            <div className="d-flex p-3 flex-column">
              {containers?.map((container, index) => (
                <>
                  <ContainerFragment key={container.uuid}>
                    {isView ? (
                      <ContainerInfoView containerInfo={container} />
                    ) : (
                      <ContainerDetails
                        index={index}
                        containerInfo={container}
                        handleDelete={handleDeleteContainer}
                      />
                    )}
                  </ContainerFragment>
                  <Divider />
                </>
              ))}
            </div>
            {!isView && !shipment?.shipmentTrackingId ? (
              <ButtonSection item container xs={12} alignItems="center" justifyContent="flex-start">
                <AddContainerButton
                  onClick={handleAddContainer}
                  customColor={Color.DEFAULT}
                  startIcon={<BiPlus />}
                >
                  {translate("addAnotherContainer")}
                </AddContainerButton>
              </ButtonSection>
            ) : null}
          </div>
        </CollapseBody>
      </CollapsablePanel>
    </MainContainer>
  );
};

export default Containers;
